// ./src/components/generic/FontWeightSelector/FontWeightSelector.tsx
import React from "react";
import { Select, SelectProps } from "@mantine/core";

const FONT_WEIGHT_OPTIONS = [
  {
    value: "inherit",
    label: "Inherit",
    description: "",
  },
  {
    value: "100",
    label: "Thin",
    description: "100 - Very light weight",
  },
  {
    value: "200",
    label: "Extra Light",
    description: "200 - Extra light weight",
  },
  {
    value: "300",
    label: "Light",
    description: "300 - Light weight",
  },
  {
    value: "400",
    label: "Normal",
    description: "400 - Regular weight",
  },
  {
    value: "500",
    label: "Medium",
    description: "500 - Medium weight",
  },
  {
    value: "600",
    label: "Semi Bold",
    description: "600 - Semi bold weight",
  },
  {
    value: "700",
    label: "Bold",
    description: "700 - Bold weight",
  },
  {
    value: "800",
    label: "Extra Bold",
    description: "800 - Extra bold weight",
  },
  {
    value: "900",
    label: "Black",
    description: "900 - Heaviest weight",
  },
] as const;

export interface FontWeightSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current font weight value */
  value: string | null | undefined;

  /** Callback when font weight changes */
  onChange: (value: string | null) => void;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;

  /** Allow custom font weight input */
  allowCustom?: boolean;
}

export const FontWeightSelector: React.FC<FontWeightSelectorProps> = ({
  value,
  onChange,
  withDescriptions = false,
  allowCustom = false,
  placeholder = "Select font weight",
  label,
  description,
  error,
  ...selectProps
}) => {
  // Prepare select data with optional descriptions
  let selectData: any[] = FONT_WEIGHT_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // If allowCustom and value is not in presets, add it to the data
  if (allowCustom && value) {
    const isPreset = FONT_WEIGHT_OPTIONS.some((opt) => opt.value === value);
    if (!isPreset) {
      selectData = [
        ...selectData,
        {
          value,
          label: `Custom: ${value}`,
        },
      ];
    }
  }

  return (
    <Select
      {...selectProps}
      label={label}
      description={description}
      error={error}
      data={selectData}
      value={value || null}
      onChange={onChange}
      placeholder={placeholder}
      searchable={allowCustom}
      clearable={false}
      allowDeselect={false}
    />
  );
};

// Export font weight options for use in other components
export { FONT_WEIGHT_OPTIONS };
